import React, { useState, useEffect } from 'react';
import { getTechPageData } from '../services/techService';
import { getStrapiMedia, renderSafeText } from '../lib/strapi';


const Technology = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const result = await getTechPageData();
      setData(result);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="py-40 text-center flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-widya-green border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Loading Technology...</p>
      </div>
    );
  }

  const heroImageUrl = getStrapiMedia(data.hero_Image);

  // Prepare WIDYA Connect Images
  const wcLogos = data.widya_connect_logo || [];
  const wcImg1 = wcLogos.find(i => i.name?.includes('Img1')) || wcLogos[0];
  const wcImg2 = wcLogos.find(i => i.name?.includes('Img2')) || wcLogos[1];
  const wcImg3 = wcLogos.find(i => i.name?.includes('Img3')) || wcLogos[2];

  // Prepare Harvest Flow Images
  const hfLogos = data.harvest_flow_logo || [];
  const hfImg1 = hfLogos.find(i => i.name?.includes('Imag1') || i.name?.includes('Img1')) || hfLogos[0];
  const hfImg2 = hfLogos.find(i => i.name?.includes('Img2')) || hfLogos[1];

  const harvestFlowBullets = data.harvest_flow_description
    ? data.harvest_flow_description.split('\n\n').map(s => s.trim()).filter(Boolean)
    : [];

  // Helper to make specific user phrases bolder to match design perfectly
  const renderBoldText = (text) => {
    if (text.startsWith('WIDYA HarvestFlow is an innovative coordination engine')) {
      return <><strong className="font-bold text-black">WIDYA HarvestFlow is an innovative coordination engine</strong>{text.substring(54)}</>;
    }
    if (text.startsWith('This "digital first, physical last" strategy ensures')) {
      return <><strong className="font-bold text-black">This &quot;digital first, physical last&quot; strategy ensures</strong>{text.substring(52)}</>;
    }
    return text;
  };

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden min-h-[60vh] lg:min-h-[85vh] flex items-center justify-center">
        {heroImageUrl ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${heroImageUrl})` }}
            />
            <div className="absolute inset-0 bg-black/40" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gray-50" />
        )}
        <div className="container mx-auto px-6 text-center relative z-10 w-full max-w-[1200px]">
          <h1 className={`text-[46px] md:text-[64px] lg:text-[80px] font-bold tracking-tight leading-[1.1] max-w-5xl mx-auto font-heading ${heroImageUrl ? 'text-white' : 'text-widya-charcoal'}`}>
            {renderSafeText(data.hero_title)}
          </h1>
        </div>
      </section>

      {/* WIDYAConnect Section */}
      <section className="py-24 px-6 bg-white border-b border-gray-100">
        <div className="container mx-auto max-w-[1200px]">
          <div className="mb-12">
            <h2 className="text-[32px] md:text-[40px] font-bold leading-tight tracking-[-1px] text-black font-heading">
              <span className="block text-[40px] md:text-[46px]">WIDYAConnect</span>
              <span className="block mt-2">Platform Overview</span>
            </h2>
          </div>
          
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 mb-16 items-start">
            <div className="lg:w-1/2 pt-4">
              <p className="text-[#333333] text-[16px] md:text-[18px] font-normal leading-relaxed tracking-[-0.5px]">
                {renderSafeText(data.widya_connect_description)}
              </p>
            </div>
            <div className="lg:w-1/2">
              {wcImg1 && (
                <img src={getStrapiMedia(wcImg1)} alt="WIDYA Connect Hub" className="w-full h-auto object-contain" />
              )}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
            <div className="lg:w-1/2">
              {wcImg2 && (
                <img src={getStrapiMedia(wcImg2)} alt="Tech Stack" className="w-full h-auto object-contain shadow-sm border border-gray-100" />
              )}
            </div>
            <div className="lg:w-1/2">
              {wcImg3 && (
                <img src={getStrapiMedia(wcImg3)} alt="Ecosystem Engine" className="w-full h-auto object-contain shadow-sm border border-gray-100" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* WIDYA HarvestFlow Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-[1200px]">
          <div className="mb-16">
            <h2 className="text-[32px] md:text-[40px] font-bold leading-tight tracking-[-1px] text-black font-heading">
              <span className="block text-[40px] md:text-[46px]">WIDYA HarvestFlow</span>
              <span className="block mt-2">Platform Overview</span>
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
            <div className="lg:w-[45%]">
              <ul className="space-y-6">
                {harvestFlowBullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="mr-3 mt-1 text-black text-2xl leading-none">•</span>
                    <p className="text-[#333333] text-[16px] md:text-[18px] font-normal leading-relaxed tracking-[-0.5px]">
                      {renderBoldText(bullet)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:w-[55%] flex flex-col gap-8">
              {hfImg1 && (
                <div className="bg-[#f0ede6] p-4 lg:p-8 flex items-center justify-center">
                  <img src={getStrapiMedia(hfImg1)} alt="Status Quo vs With WIDYA" className="w-full h-auto object-contain mix-blend-multiply" />
                </div>
              )}
              {hfImg2 && (
                <div className="flex justify-center -mt-4">
                  <img src={getStrapiMedia(hfImg2)} alt="Ecosystem Matrix" className="w-full max-w-[480px] h-auto object-contain" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Technology;